/**
 * KPop Demon Hunters — Simple Bundles GWP Display
 *
 * PRESENTATION ONLY.
 * - Does NOT add/remove/change cart items.
 * - Does NOT call Shopify cart endpoints.
 * - Does NOT reference the underlying GWP sticker variant ID.
 * - Simple Bundles remains the source of truth for the actual gift component.
 * - Does NOT create a MutationObserver.
 *
 * Cart DOM coordination:
 * - The threshold GWP/cart coordinator owns SlideCart DOM observation.
 * - This module listens for `woobles:cart-dom-updated` and re-renders only then.
 * - Direct quantity interactions are handled with lightweight delegated events.
 * - All rendering is idempotent and coalesced with requestAnimationFrame.
 */

(() => {
  'use strict';

  if (window.__KPDH_SIMPLE_BUNDLES_GWP__) return;
  window.__KPDH_SIMPLE_BUNDLES_GWP__ = true;

  const QUALIFYING_VARIANT_IDS = new Set([
    '52936090812727', // Better Than Perfect Bundle
    '52936090419511', // Small but Mighty Bundle
    '52936101036343', // We Are Hunters Bundle
    '52936089534775', // Spice Queen Bundle
    '52936098939191', // Heroes and Helpers Bundle
    '52936086913335', // Heroes of HUNTR/X Bundle
    '52936012202295', // Magical Messengers Bundle
    '52936138162487', // Side Eye Superstar Bundle
  ]);

  const GIFT = {
    title: 'KPop Demon Hunters Sticker Pack',
    unitValue: 10,
    imageUrl:
      'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/53WSP-KPOP-StickerPack-Loose-RET_1.jpg?v=1785896608',
  };

  const ROOT_SELECTOR = '#slidecarthq';
  const ITEMS_SELECTOR = '.items';
  const CART_ITEM_SELECTOR = '.item[data-variant-id]';
  const GIFT_CLASS = 'kpd-gwp-display';
  const STYLE_ID = 'kpd-gwp-simple-bundles-styles';
  const CART_DOM_UPDATED_EVENT = 'woobles:cart-dom-updated';

  // Shared coordinator contract: mutations inside these visual rows are ignored
  // by the cart observer, preventing presentation code from feeding back into
  // cart reconciliation.
  const PRESENTATION_ONLY_ATTR = 'data-cart-presentation-only';

  const STYLES = `
    #slidecarthq .${GIFT_CLASS} {
      display: grid;
      grid-template-columns: 76px minmax(0, 1fr) auto;
      align-items: center;
      gap: 12px;
      margin: 0 4px 8px;
      padding: 10px;
      border-radius: 0.625rem;
      background: #E6DFF0;
      color: var(--black-olive, #3A3A3A);
    }

    #slidecarthq .${GIFT_CLASS}__image {
      display: block;
      width: 76px;
      height: 76px;
      object-fit: cover;
      border-radius: 4px;
    }

    #slidecarthq .${GIFT_CLASS}__image[hidden] {
      display: none;
    }

    #slidecarthq .${GIFT_CLASS}:has(.${GIFT_CLASS}__image[hidden]) {
      grid-template-columns: minmax(0, 1fr) auto;
    }

    #slidecarthq .${GIFT_CLASS}__copy {
      min-width: 0;
    }

    #slidecarthq .${GIFT_CLASS}__label {
      display: block;
      margin-bottom: 2px;
      font-family: "Acherus Grotesque", sans-serif;
      font-size: 1rem;
      font-weight: 800;
      line-height: 1.2;
      letter-spacing: 0.04em;
      color: #5F477D;
    }

    #slidecarthq .${GIFT_CLASS}__title-row {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 5px;
    }

    #slidecarthq .${GIFT_CLASS}__title {
      font-size: 13.6px;
      font-weight: 600;
      line-height: 1.25;
      color: var(--black-olive, #3A3A3A);
    }

    #slidecarthq .${GIFT_CLASS}__quantity {
      flex: 0 0 auto;
      font-size: 12.5px;
      font-weight: 600;
    }

    #slidecarthq .${GIFT_CLASS}__price {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      white-space: nowrap;
      line-height: 1.2;
    }

    #slidecarthq .${GIFT_CLASS}__price s {
      color: #7A7A7A;
      opacity: 0.8;
      text-decoration-line: line-through;
      text-decoration-thickness: 1.5px;
      font-size: 13.6px;
    }

    #slidecarthq .${GIFT_CLASS}__price span {
      color: #FF2451;
      font-weight: 600;
      font-size: 13.6px;
    }

    @media (max-width: 420px) {
      #slidecarthq .${GIFT_CLASS} {
        grid-template-columns: 68px minmax(0, 1fr) auto;
        gap: 9px;
      }

      #slidecarthq .${GIFT_CLASS}__image {
        width: 68px;
        height: 68px;
      }
    }
  `;

  let renderFrame = 0;

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  function scheduleRender() {
    if (renderFrame) return;

    renderFrame = window.requestAnimationFrame(() => {
      renderFrame = 0;
      renderGiftRows();
    });
  }

  function getItemsContainer() {
    return document.querySelector(`${ROOT_SELECTOR} ${ITEMS_SELECTOR}`);
  }

  function getParentKey(item, fallbackIndex) {
    return (
      item.dataset.lineItemKey || item.dataset.lineItemId || `${item.dataset.variantId || 'unknown'}-${fallbackIndex}`
    );
  }

  function getParentQuantity(item) {
    const input = item.querySelector('.quantity-selector input');
    const parsed = Number.parseInt(input?.value || '', 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function createGiftRow(parentKey) {
    const row = document.createElement('div');
    row.className = GIFT_CLASS;
    row.dataset.kpdGwpParentKey = parentKey;
    row.setAttribute(PRESENTATION_ONLY_ATTR, 'true');
    row.setAttribute('role', 'note');
    row.setAttribute('aria-label', `Free gift included: ${GIFT.title}`);

    const image = document.createElement('img');
    image.className = `${GIFT_CLASS}__image`;

    if (GIFT.imageUrl && !GIFT.imageUrl.startsWith('__')) {
      image.src = GIFT.imageUrl;
    } else {
      image.hidden = true;
    }

    image.alt = '';
    image.width = 56;
    image.height = 56;
    image.loading = 'lazy';
    image.decoding = 'async';

    const copy = document.createElement('div');
    copy.className = `${GIFT_CLASS}__copy`;

    const label = document.createElement('span');
    label.className = `${GIFT_CLASS}__label`;
    label.textContent = 'FREE GIFT';

    const titleRow = document.createElement('div');
    titleRow.className = `${GIFT_CLASS}__title-row`;

    const title = document.createElement('span');
    title.className = `${GIFT_CLASS}__title`;
    title.textContent = GIFT.title;

    const quantity = document.createElement('span');
    quantity.className = `${GIFT_CLASS}__quantity`;

    titleRow.append(title, quantity);
    copy.append(label, titleRow);

    const price = document.createElement('div');
    price.className = `${GIFT_CLASS}__price`;

    const compareAt = document.createElement('s');
    compareAt.textContent = `$${GIFT.unitValue}`;

    const free = document.createElement('span');
    free.textContent = 'FREE';

    price.append(compareAt, free);
    row.append(image, copy, price);

    return row;
  }

  function updateTextIfChanged(element, nextText) {
    if (element && element.textContent !== nextText) {
      element.textContent = nextText;
    }
  }

  function renderGiftRows() {
    const itemsContainer = getItemsContainer();
    if (!itemsContainer) return;

    const existingRows = new Map();

    itemsContainer.querySelectorAll(`.${GIFT_CLASS}`).forEach((row) => {
      const key = row.dataset.kpdGwpParentKey;
      if (key) existingRows.set(key, row);
    });

    const activeKeys = new Set();
    const cartItems = itemsContainer.querySelectorAll(CART_ITEM_SELECTOR);

    cartItems.forEach((item, index) => {
      const variantId = String(item.dataset.variantId || '');
      if (!QUALIFYING_VARIANT_IDS.has(variantId)) return;

      const parentKey = getParentKey(item, index);
      const quantity = getParentQuantity(item);

      activeKeys.add(parentKey);

      let giftRow = existingRows.get(parentKey);

      if (!giftRow) {
        giftRow = createGiftRow(parentKey);
      }

      updateTextIfChanged(giftRow.querySelector(`.${GIFT_CLASS}__quantity`), quantity > 1 ? `× ${quantity}` : '');

      updateTextIfChanged(giftRow.querySelector(`.${GIFT_CLASS}__price s`), `$${GIFT.unitValue * quantity}`);

      // Keep the visual gift immediately beneath its qualifying bundle.
      // This mutation is marked presentation-only, so the shared cart observer
      // intentionally ignores it.
      if (giftRow.previousElementSibling !== item) {
        item.insertAdjacentElement('afterend', giftRow);
      }
    });

    // Remove visual rows whose qualifying parent line no longer exists.
    existingRows.forEach((row, key) => {
      if (!activeKeys.has(key)) {
        row.remove();
      }
    });
  }

  function isQualifyingBundleInteraction(target) {
    if (!(target instanceof Element)) return false;

    const cartItem = target.closest(`${ROOT_SELECTOR} ${CART_ITEM_SELECTOR}`);

    if (!cartItem) return false;

    const variantId = String(cartItem.dataset.variantId || '');
    if (!QUALIFYING_VARIANT_IDS.has(variantId)) return false;

    return Boolean(target.closest('.quantity-selector'));
  }

  function handleCartInteraction(event) {
    if (!isQualifyingBundleInteraction(event.target)) return;

    // requestAnimationFrame runs after the current event stack, allowing AMP's
    // own click/input handler to update the quantity first.
    scheduleRender();
  }

  function handleCartDomUpdated() {
    scheduleRender();
  }

  function init() {
    installStyles();
    scheduleRender();

    // No MutationObserver here. The cart coordinator owns observation.
    document.addEventListener(CART_DOM_UPDATED_EVENT, handleCartDomUpdated);

    ['click', 'input', 'change'].forEach((eventName) => {
      document.addEventListener(eventName, handleCartInteraction, true);
    });

    // Additional compatibility triggers if another cart integration emits these
    // before/without the shared DOM signal.
    ['cart:added', 'cart:updated', 'cart:changed', 'cart:refresh'].forEach((eventName) => {
      document.addEventListener(eventName, scheduleRender);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
